import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import type { CrmBoardQuery, CrmCityDto, CrmDealDto, CrmDealStage } from '@upme/contracts';
import { cn } from '@upme/ui/lib/utils';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { isCrmBoardDesktopLayout, scrollBoardColumnIntoWorkspace } from '../lib/board-column-scroll';
import { CRM_STAGE_LABEL_RU, CRM_STAGE_ORDER } from '../lib/stages';
import { useMoveCrmDealStage } from '../mutations';
import {
  CRM_BOARD_INITIAL_STAGE_LIMIT,
  crmBoardOptions,
  crmBoardStageDealsOptions,
  normalizeCrmBoardQuery,
} from '../queries';
import { CrmAddLeadDialog } from './crm-add-lead-dialog';
import { CrmBoardToolbar } from './crm-board-toolbar';
import { CrmDealCard } from './crm-deal-card';
import { CrmManagerHintsStrip } from './crm-manager-hints-strip';
import { CrmUserDetailsPanel } from './crm-user-details-panel';

type Props = {
  filters: CrmBoardQuery;
  cities: ReadonlyArray<CrmCityDto>;
};

function DraggableDeal({
  deal,
  filters,
  selected,
  onSelect,
}: {
  deal: CrmDealDto;
  filters: CrmBoardQuery;
  selected: boolean;
  onSelect: () => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: deal.id,
    data: { stage: deal.stage },
  });

  return (
    <li ref={setNodeRef} className={isDragging ? 'opacity-40' : undefined}>
      <CrmDealCard
        deal={deal}
        filters={filters}
        selected={selected}
        onSelect={onSelect}
        dragAttributes={attributes}
        dragListeners={listeners}
      />
    </li>
  );
}

function formatDealCount(n: number): string {
  if (n === 1) return '1 сделка';
  if (n >= 2 && n <= 4) return `${n} сделки`;
  return `${n} сделок`;
}

function StageColumn({
  stage,
  initialDeals,
  totalCount,
  filters,
  selectedDealId,
  workspaceStage,
  columnRef,
  onSelectDeal,
}: {
  stage: CrmDealStage;
  initialDeals: CrmDealDto[];
  totalCount: number;
  filters: CrmBoardQuery;
  selectedDealId: string | null;
  workspaceStage: CrmDealStage | null;
  columnRef: (el: HTMLElement | null) => void;
  onSelectDeal: (deal: CrmDealDto) => void;
}) {
  const queryClient = useQueryClient();
  const { setNodeRef, isOver } = useDroppable({ id: stage });
  const listRef = useRef<HTMLUListElement>(null);
  const [extraDeals, setExtraDeals] = useState<CrmDealDto[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);

  const initialIdsKey = useMemo(() => initialDeals.map((d) => d.id).join(','), [initialDeals]);
  useEffect(() => {
    setExtraDeals([]);
  }, [filters, initialIdsKey]);

  const deals = useMemo(() => {
    const seen = new Set<string>();
    const out: CrmDealDto[] = [];
    for (const deal of [...initialDeals, ...extraDeals]) {
      if (seen.has(deal.id)) continue;
      seen.add(deal.id);
      out.push(deal);
    }
    return out;
  }, [extraDeals, initialDeals]);

  const loadedCount = deals.length;
  const hasMore = loadedCount < totalCount;

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const normalized = normalizeCrmBoardQuery(filters);
      const page = await queryClient.fetchQuery(
        crmBoardStageDealsOptions({
          ...normalized,
          stageLimit: 0,
          stage,
          offset: loadedCount,
          limit: CRM_BOARD_INITIAL_STAGE_LIMIT,
        }),
      );
      setExtraDeals((prev) => {
        const seen = new Set([...initialDeals, ...prev].map((d) => d.id));
        return [...prev, ...page.deals.filter((d) => !seen.has(d.id))];
      });
    } finally {
      setLoadingMore(false);
    }
  }, [filters, hasMore, initialDeals, loadedCount, loadingMore, queryClient, stage]);

  useEffect(() => {
    const list = listRef.current;
    if (!list || !hasMore) return;
    const onScroll = () => {
      if (list.scrollTop + list.clientHeight >= list.scrollHeight - 48) void loadMore();
    };
    list.addEventListener('scroll', onScroll, { passive: true });
    return () => list.removeEventListener('scroll', onScroll);
  }, [hasMore, loadMore]);

  return (
    <section
      ref={(el) => {
        setNodeRef(el);
        columnRef(el);
      }}
      data-crm-stage={stage}
      data-crm-workspace={workspaceStage === stage ? 'true' : undefined}
      className={cn(
        'flex w-[min(20rem,80vw)] shrink-0 flex-col border border-border/70 bg-muted/10 sm:w-80 lg:w-[23rem]',
        workspaceStage !== null && 'lg:w-[calc((100cqw-0.625rem)/1.5)]',
        workspaceStage === stage && 'ring-1 ring-primary/25',
        isOver && 'ring-1 ring-primary/35',
      )}
      aria-label={CRM_STAGE_LABEL_RU[stage]}
    >
      <header className="border-b border-border/60 px-4 py-2.5">
        <h3 className="text-lg font-medium">{CRM_STAGE_LABEL_RU[stage]}</h3>
        <p className="text-[15px] text-muted-foreground">
          {formatDealCount(totalCount)}
          {hasMore ? ` · показано ${loadedCount}` : null}
        </p>
      </header>
      <ul
        ref={listRef}
        className="flex max-h-[min(70vh,var(--crm-viewport-h,640px))] min-h-[120px] flex-col gap-2 overflow-y-auto p-2"
      >
        {deals.map((deal) => (
          <DraggableDeal
            key={deal.id}
            deal={deal}
            filters={filters}
            selected={selectedDealId === deal.id}
            onSelect={() => onSelectDeal(deal)}
          />
        ))}
        {loadingMore ? <li className="py-2 text-center text-sm text-muted-foreground">Загрузка…</li> : null}
      </ul>
    </section>
  );
}

export function CrmBoard({ filters, cities }: Props) {
  const { data } = useSuspenseQuery(crmBoardOptions(filters));
  const move = useMoveCrmDealStage(filters);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);

  const scrollContainerRef = useRef<HTMLElement>(null);
  const columnRefs = useRef(new Map<CrmDealStage, HTMLElement>());
  const savedScrollLeftRef = useRef<number | null>(null);
  const alignedStageRef = useRef<CrmDealStage | null>(null);
  const panelOpenRef = useRef(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const byStage = useMemo(
    () =>
      CRM_STAGE_ORDER.reduce(
        (acc, stage) => {
          acc[stage] = data.deals.filter((d) => d.stage === stage);
          return acc;
        },
        {} as Record<CrmDealStage, CrmDealDto[]>,
      ),
    [data.deals],
  );

  const allLoadedDeals = useMemo(() => CRM_STAGE_ORDER.flatMap((stage) => byStage[stage]), [byStage]);

  const dealSequence = allLoadedDeals;

  const selectedDeal = selectedDealId ? allLoadedDeals.find((d) => d.id === selectedDealId) : undefined;
  const workspaceStage = selectedDeal?.stage ?? null;

  const activeDeal = activeId ? allLoadedDeals.find((d) => d.id === activeId) : undefined;

  useLayoutEffect(() => {
    const container = scrollContainerRef.current;

    if (!selectedDeal) {
      if (panelOpenRef.current && container && savedScrollLeftRef.current !== null) {
        container.scrollTo({ left: savedScrollLeftRef.current, behavior: 'smooth' });
      }
      panelOpenRef.current = false;
      alignedStageRef.current = null;
      savedScrollLeftRef.current = null;
      return;
    }

    if (!isCrmBoardDesktopLayout()) {
      panelOpenRef.current = true;
      return;
    }

    if (!container) return;

    if (!panelOpenRef.current) {
      savedScrollLeftRef.current = container.scrollLeft;
      panelOpenRef.current = true;
    }

    const stage = selectedDeal.stage;
    if (alignedStageRef.current === stage) return;

    alignedStageRef.current = stage;
    const column = columnRefs.current.get(stage);
    if (column) {
      scrollBoardColumnIntoWorkspace(container, column, 'smooth');
    }
  }, [selectedDeal]);

  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !workspaceStage || typeof ResizeObserver === 'undefined') return;
    let previousWidth = container.clientWidth;
    const observer = new ResizeObserver(() => {
      const width = container.clientWidth;
      if (width === previousWidth) return;
      previousWidth = width;
      const column = columnRefs.current.get(workspaceStage);
      if (column && isCrmBoardDesktopLayout()) scrollBoardColumnIntoWorkspace(container, column, 'instant');
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [workspaceStage]);

  function setColumnRef(stage: CrmDealStage) {
    return (el: HTMLElement | null) => {
      if (el) columnRefs.current.set(stage, el);
      else columnRefs.current.delete(stage);
    };
  }

  function onDragEnd(event: DragEndEvent) {
    setActiveId(null);
    const dealId = String(event.active.id);
    const overId = event.over?.id;
    if (!overId || typeof overId !== 'string') return;
    if (!CRM_STAGE_ORDER.includes(overId as CrmDealStage)) return;
    const newStage = overId as CrmDealStage;
    const deal = allLoadedDeals.find((d) => d.id === dealId);
    if (!deal || deal.stage === newStage) return;
    move.mutate({ dealId, stage: newStage, sourceKey: deal.sourceKey });
  }

  function closePanel() {
    setSelectedDealId(null);
  }

  return (
    <div className="relative min-w-0 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <CrmBoardToolbar filters={filters} counts={data.countsBySegment} cities={cities} />
        <CrmAddLeadDialog
          filters={filters}
          cities={cities}
          onCreated={(deal) => setSelectedDealId(deal.id)}
        />
      </div>
      <CrmManagerHintsStrip filters={filters} enabled={!selectedDeal} />
      {data.countsBySegment.all === 0 ? (
        <p className="text-sm text-muted-foreground">Нет карточек по выбранным фильтрам.</p>
      ) : null}
      <div
        className={cn(
          'flex w-full min-w-0 min-h-[420px] flex-col lg:min-h-[calc(var(--crm-viewport-h)-4.5rem)] lg:items-stretch',
          selectedDeal && 'lg:grid lg:grid-cols-[min(35.125rem,45%)_minmax(0,1fr)]',
        )}
      >
        <div
          className={cn(
            'flex min-h-0 min-w-0 flex-1 flex-col',
            selectedDeal && 'max-lg:hidden lg:[container-type:inline-size]',
          )}
        >
          <DndContext
            sensors={sensors}
            onDragStart={(e) => setActiveId(String(e.active.id))}
            onDragEnd={onDragEnd}
            onDragCancel={() => setActiveId(null)}
          >
            <section
              ref={scrollContainerRef}
              className="min-h-0 min-w-0 flex-1 overflow-x-auto overflow-y-hidden scroll-smooth pb-2 lg:overscroll-x-contain"
              aria-label="Колонки воронки CRM"
            >
              <div className="flex w-max gap-2.5 pr-2">
                {CRM_STAGE_ORDER.map((stage) => (
                  <StageColumn
                    key={stage}
                    stage={stage}
                    initialDeals={byStage[stage]}
                    totalCount={data.countsByStage[stage]}
                    filters={filters}
                    selectedDealId={selectedDealId}
                    workspaceStage={workspaceStage}
                    columnRef={setColumnRef(stage)}
                    onSelectDeal={(deal) => setSelectedDealId(deal.id)}
                  />
                ))}
                {selectedDeal ? (
                  <div
                    aria-hidden="true"
                    className="hidden shrink-0 lg:block lg:w-[calc((100cqw-0.625rem)/3+0.625rem)]"
                  />
                ) : null}
              </div>
            </section>
            <DragOverlay>
              {activeDeal ? (
                <div className="w-[min(20rem,80vw)] opacity-95 sm:w-80 lg:w-[23rem]">
                  <CrmDealCard deal={activeDeal} filters={filters} />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
        {selectedDeal ? (
          <CrmUserDetailsPanel
            deal={selectedDeal}
            sequence={dealSequence}
            boardFilters={filters}
            onClose={closePanel}
            onSelectDeal={(deal) => setSelectedDealId(deal.id)}
          />
        ) : null}
      </div>
    </div>
  );
}
